import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, Plus, Search, ArrowLeft, Megaphone, EyeOff, Flag, ShieldAlert, Loader2, Users, X, Paperclip, FileText, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";

const roleLabel = (r) => (r === "student_parent" ? "Student/Parent" : r === "admin" ? "PTG Admin" : r === "teacher" ? "Teacher" : r);
const timeLabel = (iso) => {
  if (!iso) return "";
  return formatDateTime(iso);
};
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const ACCEPT = "image/png,image/jpeg,application/pdf,.docx,.xlsx";

function Attachment({ att }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true; let obj = "";
    if (att.kind === "image") {
      api.get(`/messaging/attachments/${att.id}`, { responseType: "blob" })
        .then((r) => { if (!active) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
        .catch(() => {});
    }
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [att.id, att.kind]);

  const fetchBlob = async (download) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/messaging/attachments/${att.id}`, { responseType: "blob", params: { download: download ? 1 : 0 } });
      const obj = URL.createObjectURL(data);
      if (download) {
        const a = document.createElement("a"); a.href = obj; a.download = att.filename || "file"; a.click();
        setTimeout(() => URL.revokeObjectURL(obj), 4000);
      } else { window.open(obj, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(obj), 60000); }
    } catch { toast.error("Could not open the file."); }
    finally { setBusy(false); }
  };

  if (att.kind === "image") {
    return (
      <button onClick={() => fetchBlob(false)} className="block mt-1.5 rounded-lg overflow-hidden border border-black/10" data-testid={`attachment-${att.id}`}>
        {url ? <img src={url} alt={att.filename} className="max-h-44 max-w-[220px] object-cover" /> : <div className="h-28 w-40 bg-slate-200 animate-pulse" />}
      </button>
    );
  }
  const Icon = att.kind === "office" && /xlsx$/i.test(att.filename) ? FileSpreadsheet : FileText;
  const isPdf = att.kind === "pdf";
  return (
    <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-black/10 bg-black/5 px-2.5 py-2 max-w-[260px]" data-testid={`attachment-${att.id}`}>
      <Icon className="h-5 w-5 shrink-0 opacity-80" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold truncate">{att.filename}</div>
        <div className="text-[10px] opacity-70">{fmtSize(att.size)}</div>
      </div>
      <button disabled={busy} onClick={() => fetchBlob(!isPdf)} className="shrink-0 opacity-80 hover:opacity-100" title={isPdf ? "Open" : "Download"} data-testid={`attachment-action-${att.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isPdf ? <FileText className="h-4 w-4" /> : <Download className="h-4 w-4" />}
      </button>
    </div>
  );
}

function NewConversationDialog({ open, onClose, onStarted }) {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (query) => {
    setLoading(true);
    try { const { data } = await api.get("/messaging/contacts", { params: { q: query || "" } }); setGroups(data.groups || []); }
    catch { toast.error("Could not load contacts"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) { setQ(""); load(""); } }, [open, load]);
  useEffect(() => { if (!open) return; const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, open, load]);

  const start = async (c) => {
    try {
      const { data } = await api.post("/messaging/conversations/start", { counterpart: c.counterpart, id: c.id });
      onStarted(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start conversation"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-conversation-dialog">
        <DialogHeader><DialogTitle>New message</DialogTitle><DialogDescription>Choose a contact to start a private conversation.</DialogDescription></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or roll…" className="pl-9" data-testid="new-conversation-search" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-4 mt-1">
          {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
          {!loading && groups.every((g) => (g.contacts || []).length === 0) && (
            <p className="text-sm text-slate-500 text-center py-6">No contacts found.</p>
          )}
          {groups.map((g) => (g.contacts || []).length > 0 && (
            <div key={g.label}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{g.label}</div>
              <div className="space-y-1">
                {g.contacts.map((c) => (
                  <button key={`${c.counterpart}-${c.id}`} onClick={() => start(c)} data-testid={`contact-${c.id}`}
                    className="w-full flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 text-left hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-800 font-bold">
                      {(c.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.counterpart === "student" ? `Roll ${c.roll_number || "—"} · ${c.student_class || ""}` : roleLabel(c.role)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementDialog({ open, onClose, onSent }) {
  const [classes, setClasses] = useState([]);
  const [klass, setKlass] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setKlass(""); setTitle(""); setBody(""); setAudience(null); setAtts([]);
    api.get("/messaging/classes").then(({ data }) => setClasses(data.classes || [])).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!klass) { setAudience(null); return; }
    api.get("/messaging/announcements/audience", { params: { klass } }).then(({ data }) => setAudience(data.count)).catch(() => setAudience(null));
  }, [klass]);
  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/messaging/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAtts((p) => [...p, data]); }
      catch (err) { toast.error(err.response?.data?.detail || `Could not upload ${f.name}`); }
    }
    setUploading(false);
  };
  const send = async () => {
    if (!klass || !title.trim() || !body.trim()) { toast.error("Class, title and message are required."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/messaging/announcements", { klass, title: title.trim(), body: body.trim(), client_token: newToken(), attachment_ids: atts.map((a) => a.id) });
      toast.success(`Announcement sent to ${data.recipients} student(s) in ${klass}.`);
      onSent?.(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send announcement"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="announcement-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-blue-700" /> New announcement</DialogTitle><DialogDescription>Send a read-only notice to one class/batch.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Class / Batch</Label>
            <Select value={klass} onValueChange={setKlass}>
              <SelectTrigger data-testid="announcement-class"><SelectValue placeholder="Select a class" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => (<SelectItem key={c.klass} value={c.klass}>{c.klass} ({c.count})</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {audience != null && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800 flex items-center gap-2" data-testid="announcement-audience">
              <Users className="h-4 w-4" /> This will reach <b>{audience}</b> student{audience === 1 ? "" : "s"} in {klass}. Only this class will receive it.
            </div>
          )}
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Holiday notice" data-testid="announcement-title" /></div>
          <div><Label>Message</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write the announcement…" data-testid="announcement-body" /></div>
          <div>
            <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={pickFiles} data-testid="announcement-attach-input" />
            <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="announcement-attach-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Paperclip className="h-4 w-4 mr-1.5" />} Attach file
            </Button>
            {atts.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {atts.map((a) => (
                  <div key={a.id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 pl-2 pr-1 py-1 text-xs">
                    <span className="max-w-[120px] truncate">{a.filename}</span>
                    <button onClick={() => setAtts((p) => p.filter((x) => x.id !== a.id))} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500">Announcements are read-only for students/parents. They can reply by starting a private conversation with you or a teacher.</p>
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={sending || uploading || !klass} className="bg-blue-900 hover:bg-blue-950" data-testid="announcement-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Megaphone className="h-4 w-4 mr-1.5" /> Send to {audience ?? "…"} </>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupsDialog({ open, onClose }) {
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [name, setName] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([api.get("/messaging/groups"), api.get("/messaging/contacts")]);
      setGroups(g.data.groups || []);
      const t = (c.data.groups || []).find((x) => x.label === "Teachers");
      setTeachers(t ? t.contacts : []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (open) { setName(""); setSel({}); load(); } }, [open, load]);
  const ensureStaff = async () => { setBusy(true); try { await api.post("/messaging/groups/ensure-staff"); toast.success("PTG Staff group is up to date."); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const create = async () => {
    const member_ids = Object.keys(sel).filter((k) => sel[k]);
    if (!name.trim()) { toast.error("Group name required"); return; }
    setBusy(true);
    try { await api.post("/messaging/groups", { name: name.trim(), member_ids }); toast.success("Group created"); setName(""); setSel({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="groups-dialog">
        <DialogHeader><DialogTitle>Staff groups</DialogTitle><DialogDescription>Groups are visible only to staff members — never to students/parents.</DialogDescription></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div className="text-sm">Create/refresh the <b>PTG Staff</b> group (all admins + teachers).</div>
            <Button size="sm" variant="outline" onClick={ensureStaff} disabled={busy} data-testid="ensure-staff-btn">Refresh PTG Staff</Button>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="font-semibold text-sm">New class/batch staff group</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Std 10 Teachers" data-testid="group-name-input" />
            <div className="text-xs text-slate-500">Members (staff):</div>
            <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
              {teachers.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm rounded px-2 py-1 hover:bg-slate-50">
                  <Checkbox checked={!!sel[t.id]} onCheckedChange={(v) => setSel((p) => ({ ...p, [t.id]: !!v }))} data-testid={`group-member-${t.id}`} />
                  <span className="truncate">{t.name}</span>
                </label>
              ))}
            </div>
            <Button size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={create} disabled={busy} data-testid="group-create-btn">Create group</Button>
          </div>
          <div className="space-y-2">
            <div className="font-semibold text-sm">Existing groups</div>
            {groups.map((g) => (
              <div key={g.id} className="rounded-lg border border-slate-200 p-3" data-testid={`group-${g.id}`}>
                <div className="font-semibold text-sm">{g.name} <span className="text-xs text-slate-400">· {g.members.length} members</span></div>
                <div className="text-xs text-slate-500 truncate">{g.members.map((m) => m.name).join(", ")}</div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ open, onClose }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/messaging/settings").then(({ data }) => setS(data)).catch(() => {}); }, [open]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/messaging/settings", { max_file_mb: s.max_file_mb, max_files: s.max_files, email_alerts_enabled: s.email_alerts_enabled }); setS((p) => ({ ...p, ...data })); toast.success("Settings saved"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="settings-dialog">
        <DialogHeader><DialogTitle>Messaging settings</DialogTitle><DialogDescription>Attachment limits and optional email alerts.</DialogDescription></DialogHeader>
        {s && (
          <div className="space-y-4">
            <div><Label>Max file size (MB)</Label><Input type="number" min="1" max="25" value={s.max_file_mb} onChange={(e) => setS((p) => ({ ...p, max_file_mb: parseInt(e.target.value) || 1 }))} data-testid="settings-max-mb" /></div>
            <div><Label>Max files per message</Label><Input type="number" min="1" max="10" value={s.max_files} onChange={(e) => setS((p) => ({ ...p, max_files: parseInt(e.target.value) || 1 }))} data-testid="settings-max-files" /></div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><div className="text-sm font-semibold">Email alerts</div><div className="text-xs text-slate-500">Off by default. Minimal summary + sign-in link only (no message content).</div></div>
              <Switch checked={!!s.email_alerts_enabled} onCheckedChange={(v) => setS((p) => ({ ...p, email_alerts_enabled: v }))} data-testid="settings-email-toggle" />
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Allowed: JPG, PNG, PDF, DOCX, XLSX. Files are access-controlled, not antivirus-scanned (AV scanning is not available in this setup).</p>
            <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 w-full" data-testid="settings-save">Save</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportsDialog({ open, onClose }) {
  const [reports, setReports] = useState([]);
  useEffect(() => { if (open) api.get("/messaging/reports", { params: { status: "open" } }).then(({ data }) => setReports(data || [])).catch(() => {}); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reports-dialog">
        <DialogHeader><DialogTitle>Reported messages</DialogTitle><DialogDescription>Messages flagged to PTG Admin by users.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {reports.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No reports.</p>}
          {reports.map((r) => (
            <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`report-row-${r.id}`}>
              <div className="font-semibold">{r.reporter_name} <span className="text-xs text-slate-400">({r.reporter_role})</span></div>
              <div className="text-slate-600">{r.reason || "No reason given"}</div>
              <div className="text-[11px] text-slate-400 mt-1">{timeLabel(r.at)} · msg {r.message_id?.slice(0, 8)}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastGroupsDialog({ open, onClose, scope, onChange }) {
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const loadStudents = useCallback(async (query) => {
    setStudentsLoaded(false);
    try {
      const { data } = await api.get("/messaging/contacts", { params: { q: query || "" } });
      const grp = (data.groups || []).find((g) => g.label.startsWith("My Students") || g.label.startsWith("Students"));
      setStudents(grp ? grp.contacts : []);
    } catch { /* ignore */ }
    finally { setStudentsLoaded(true); }
  }, []);
  const loadGroups = useCallback(async () => {
    try { const { data } = await api.get("/messaging/broadcast-groups"); setGroups(data.groups || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (open) { setName(""); setSel({}); setQ(""); loadStudents(""); loadGroups(); } }, [open, loadStudents, loadGroups]);
  useEffect(() => { if (open) { const t = setTimeout(() => loadStudents(q), 300); return () => clearTimeout(t); } }, [q, open, loadStudents]);
  const create = async () => {
    const member_ids = Object.keys(sel).filter((k) => sel[k]);
    if (!name.trim()) { toast.error("Group name required"); return; }
    if (!member_ids.length) { toast.error("Select at least one student"); return; }
    setBusy(true);
    try { await api.post("/messaging/broadcast-groups", { name: name.trim(), member_ids }); toast.success("Group created. Post a message and all members receive it."); setName(""); setSel({}); loadGroups(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="broadcast-groups-dialog">
        <DialogHeader><DialogTitle>Student groups</DialogTitle><DialogDescription>Broadcast to many {scope === "teacher" ? "of your assigned " : ""}students at once. Students see your posts and the group name, never each other — replies come to you privately.</DialogDescription></DialogHeader>
        <div className="space-y-4 max-h-[62vh] overflow-y-auto">
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="font-semibold text-sm">New student group</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Std 10 Batch A" data-testid="bgroup-name-input" />
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…" className="pl-9 h-9" data-testid="bgroup-student-search" />
            </div>
            <div className="max-h-44 overflow-y-auto space-y-1">
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm rounded px-2 py-1 hover:bg-slate-50">
                  <Checkbox checked={!!sel[s.id]} onCheckedChange={(v) => setSel((p) => ({ ...p, [s.id]: !!v }))} data-testid={`bgroup-member-${s.id}`} />
                  <span className="truncate">{s.name} <span className="text-xs text-slate-400">· {s.roll_number} · {s.student_class}</span></span>
                </label>
              ))}
              {students.length === 0 && studentsLoaded && <p className="text-xs text-slate-400 px-2 py-2">No students found.</p>}
              {!studentsLoaded && <p className="text-xs text-slate-400 px-2 py-2">Loading students…</p>}
            </div>
            <Button size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={create} disabled={busy} data-testid="bgroup-create-btn">Create group</Button>
          </div>
          <div className="space-y-2">
            <div className="font-semibold text-sm">Your groups</div>
            {groups.length === 0 && <p className="text-xs text-slate-400">No student groups yet.</p>}
            {groups.map((g) => (
              <div key={g.id} className="rounded-lg border border-slate-200 p-3" data-testid={`bgroup-${g.id}`}>
                <div className="font-semibold text-sm">{g.name} <span className="text-xs text-slate-400">· {g.members.length} students</span></div>
                <div className="text-xs text-slate-500 truncate">{g.members.map((m) => m.name).join(", ")}</div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Messages({ scope }) {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);     // conversation summary
  const [detail, setDetail] = useState(null);         // full detail with messages
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [q, setQ] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [maxFiles, setMaxFiles] = useState(5);
  const fileRef = useRef(null);
  const [showNew, setShowNew] = useState(false);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showBGroups, setShowBGroups] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { api.get("/messaging/settings").then(({ data }) => setMaxFiles(data.max_files || 5)).catch(() => {}); }, []);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (atts.length + files.length > maxFiles) { toast.error(`You can attach up to ${maxFiles} files per message.`); return; }
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/messaging/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAtts((prev) => [...prev, data]);
      } catch (err) { toast.error(err.response?.data?.detail || `Could not upload ${f.name}`); }
    }
    setUploading(false);
  };
  const removeAtt = (id) => setAtts((prev) => prev.filter((a) => a.id !== id));

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/conversations", { params: { unread: unreadOnly ? 1 : 0, q } });
      setConversations(data.conversations || []);
    } catch { /* interceptor handles auth */ }
    finally { setLoadingList(false); }
  }, [unreadOnly, q]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { const t = setInterval(loadList, 20000); return () => clearInterval(t); }, [loadList]);

  const openConversation = useCallback(async (conv) => {
    setSelected(conv);
    setLoadingThread(true);
    setDetail(null);
    setBody(""); setAtts([]);
    try {
      const { data } = await api.get(`/messaging/conversations/${conv.id}`);
      setDetail(data);
      setConversations((prev) => prev.map((c) => (c.id === conv.id ? { ...c, unread: 0 } : c)));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 50);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open conversation"); }
    finally { setLoadingThread(false); }
  }, []);

  const refreshThread = useCallback(async () => {
    if (!selected) return;
    try { const { data } = await api.get(`/messaging/conversations/${selected.id}`); setDetail(data); } catch { /* ignore */ }
  }, [selected]);
  useEffect(() => { if (!selected) return; const t = setInterval(refreshThread, 12000); return () => clearInterval(t); }, [selected, refreshThread]);

  const send = async () => {
    const text = body.trim();
    if ((!text && atts.length === 0) || !detail) return;
    setSending(true);
    const token = newToken();
    try {
      await api.post(`/messaging/conversations/${detail.id}/messages`, { body: text, client_token: token, attachment_ids: atts.map((a) => a.id) });
      setBody(""); setAtts([]);
      await refreshThread();
      loadList();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send message"); }
    finally { setSending(false); }
  };

  const report = async (mid) => {
    const reason = window.prompt("Report this message to PTG Admin. Add a reason (optional):", "");
    if (reason === null) return;
    try { await api.post("/messaging/report", { message_id: mid, reason }); toast.success("Reported to PTG Admin."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not report"); }
  };
  const hide = async (mid) => {
    const reason = window.prompt("Hide this message. A reason is required (kept in the audit log):", "");
    if (!reason) return;
    try { await api.post(`/messaging/messages/${mid}/hide`, { reason }); toast.success("Message hidden."); refreshThread(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not hide"); }
  };

  const onStarted = (conv) => {
    setShowNew(false);
    loadList();
    openConversation({ ...conv, unread: 0 });
  };

  const replyPrivately = async (target) => {
    try {
      const { data } = await api.post("/messaging/conversations/start", { counterpart: target.counterpart, id: target.id });
      loadList();
      openConversation({ ...data, unread: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open private chat"); }
  };

  const empty = !loadingList && conversations.length === 0;

  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="messages-panel">
      <div className="flex flex-col md:flex-row h-[70vh] min-h-[480px]">
        {/* LIST PANE */}
        <div className={`border-b md:border-b-0 md:border-r border-slate-200 flex-col min-h-0 md:w-80 md:shrink-0 md:flex ${selected ? "hidden" : "flex flex-1 md:flex-none"}`}>
          <div className="p-3 border-b border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages…" className="pl-9 h-9" data-testid="messages-search" />
              </div>
              <Button size="icon" className="h-9 w-9 bg-blue-900 hover:bg-blue-950 shrink-0" onClick={() => setShowNew(true)} data-testid="messages-new-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant={unreadOnly ? "default" : "outline"} className={unreadOnly ? "bg-blue-900 hover:bg-blue-950 h-8" : "h-8"} onClick={() => setUnreadOnly((v) => !v)} data-testid="messages-unread-filter">Unread</Button>
              {(scope === "teacher" || scope === "admin") && (
                <Button size="sm" variant="outline" className="h-8" onClick={() => setShowBGroups(true)} data-testid="messages-student-groups-btn"><Users className="h-4 w-4 mr-1.5" /> Student Groups</Button>
              )}
              {scope === "admin" && (
                <>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowAnnounce(true)} data-testid="messages-announce-btn"><Megaphone className="h-4 w-4 mr-1.5" /> Announce</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowGroups(true)} data-testid="messages-groups-btn"><Users className="h-4 w-4 mr-1.5" /> Groups</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowReports(true)} title="Reported messages" data-testid="messages-reports-btn"><Flag className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowSettings(true)} title="Messaging settings" data-testid="messages-settings-btn"><ShieldAlert className="h-4 w-4" /></Button>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
            {loadingList && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
            {empty && (
              <div className="text-center text-slate-500 px-6 py-12">
                <MessageCircle className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm">No conversations yet.</p>
                <Button size="sm" className="mt-3 bg-blue-900 hover:bg-blue-950" onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-1.5" /> Start a message</Button>
              </div>
            )}
            {conversations.map((c) => (
              <button key={c.id} onClick={() => openConversation(c)} data-testid={`conversation-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected?.id === c.id ? "bg-blue-50" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                    {c.type === "announcement" && <Megaphone className="h-4 w-4 text-blue-600 shrink-0" />}
                    {c.title}
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">{timeLabel(c.last_message_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <div className="text-xs text-slate-500 truncate">{c.last_message || c.subtitle}</div>
                  {c.unread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold shrink-0" data-testid={`unread-${c.id}`}>{c.unread}</span>}
                </div>
                <div className="flex gap-1.5 mt-1">
                  {c.supervising && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 py-0">Supervising</Badge>}
                  {c.official && c.type !== "announcement" && <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500 py-0">Official</Badge>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* THREAD PANE */}
        <div className={`flex-col min-h-0 flex-1 ${selected ? "flex" : "hidden md:flex"}`}>
          {!selected && <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation to start messaging.</div>}
          {selected && (
            <>
              <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => { setSelected(null); setDetail(null); }} data-testid="thread-back"><ArrowLeft className="h-4 w-4" /></Button>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                    {selected.type === "announcement" && <Megaphone className="h-4 w-4 text-blue-600" />}
                    {detail?.title || selected.title}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{detail?.subtitle || selected.subtitle}</div>
                </div>
              </div>

              {detail?.supervising && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex items-center gap-1.5" data-testid="supervising-banner">
                  <ShieldAlert className="h-4 w-4 shrink-0" /> You are supervising this official student–teacher conversation (you are not a direct participant).
                </div>
              )}
              {detail?.official && !detail?.supervising && scope === "student" && (
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs text-slate-500" data-testid="official-banner">
                  This is an official PTG conversation. PTG Admin may view it for your safety.
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="message-thread">
                {loadingThread && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
                {detail?.messages?.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No messages yet. Say hello 👋</p>}
                {detail?.messages?.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.mine ? "bg-blue-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                      {!m.mine && <div className="text-[11px] font-semibold opacity-70 mb-0.5">{m.sender_name} · {roleLabel(m.sender_role)}</div>}
                      {m.hidden ? (
                        <div className="italic text-xs opacity-70 flex items-center gap-1.5"><EyeOff className="h-3.5 w-3.5" /> This message was removed by PTG Admin{m.hidden_reason ? ` (${m.hidden_reason})` : ""}.</div>
                      ) : (
                        <>
                          {m.body && <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>}
                          {(m.attachments || []).map((a) => <Attachment key={a.id} att={a} />)}
                        </>
                      )}
                      <div className={`text-[10px] mt-1 flex items-center gap-2 ${m.mine ? "text-blue-200" : "text-slate-400"}`}>
                        <span>{timeLabel(m.created_at)}</span>
                        {!m.mine && !m.hidden && <button onClick={() => report(m.id)} className="hover:underline inline-flex items-center gap-0.5" data-testid={`report-${m.id}`}><Flag className="h-3 w-3" /> Report</button>}
                        {detail?.can_moderate && !m.hidden && <button onClick={() => hide(m.id)} className="hover:underline inline-flex items-center gap-0.5" data-testid={`hide-${m.id}`}><EyeOff className="h-3 w-3" /> Hide</button>}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {detail?.read_only ? (
                detail?.reply_target ? (
                  <div className="p-3 border-t border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="broadcast-reply-bar">
                    <span className="text-xs text-slate-500">Group broadcast — your reply goes privately to {detail.reply_target.name}, no other student sees it.</span>
                    <Button size="sm" className="bg-blue-900 hover:bg-blue-950 shrink-0" onClick={() => replyPrivately(detail.reply_target)} data-testid="reply-private-btn"><Send className="h-4 w-4 mr-1.5" /> Message {detail.reply_target.name} privately</Button>
                  </div>
                ) : (
                  <div className="p-3 border-t border-slate-200 text-center text-xs text-slate-500 bg-white">
                    This is a read-only announcement. To ask a question, start a private conversation with your teacher or PTG Admin.
                  </div>
                )
              ) : detail && (
                <div className="border-t border-slate-200 bg-white">
                  {atts.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-3 pt-2.5" data-testid="composer-attachments">
                      {atts.map((a) => (
                        <div key={a.id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 pl-2 pr-1 py-1 text-xs" data-testid={`composer-att-${a.id}`}>
                          {a.kind === "image" ? <Paperclip className="h-3.5 w-3.5 text-slate-500" /> : a.kind === "pdf" ? <FileText className="h-3.5 w-3.5 text-slate-500" /> : <FileSpreadsheet className="h-3.5 w-3.5 text-slate-500" />}
                          <span className="max-w-[120px] truncate">{a.filename}</span>
                          <span className="text-slate-400">{fmtSize(a.size)}</span>
                          <button onClick={() => removeAtt(a.id)} className="text-slate-400 hover:text-red-500" data-testid={`composer-att-remove-${a.id}`}><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="p-3 flex items-end gap-2">
                    <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={pickFiles} data-testid="attach-input" />
                    <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" disabled={uploading || atts.length >= maxFiles} onClick={() => fileRef.current?.click()} title="Attach file" data-testid="attach-btn">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder="Type a message…"
                      className="resize-none min-h-[40px] max-h-32" data-testid="message-input"
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                    <Button onClick={send} disabled={sending || uploading || (!body.trim() && atts.length === 0)} className="bg-blue-900 hover:bg-blue-950 shrink-0 h-10" aria-label="Send message" data-testid="message-send">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="px-3 pb-2 text-[10px] text-slate-400">JPG, PNG, PDF, DOCX, XLSX · up to {maxFiles} files. Files are access-controlled (not AV-scanned).</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <NewConversationDialog open={showNew} onClose={() => setShowNew(false)} onStarted={onStarted} />
      <AnnouncementDialog open={showAnnounce} onClose={() => setShowAnnounce(false)} onSent={loadList} />
      <GroupsDialog open={showGroups} onClose={() => setShowGroups(false)} />
      <BroadcastGroupsDialog open={showBGroups} onClose={() => setShowBGroups(false)} scope={scope} onChange={loadList} />
      <SettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />
      <ReportsDialog open={showReports} onClose={() => setShowReports(false)} />
    </div>
  );
}

export function useUnreadMessages() {
  const [count, setCount] = useState(0);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/messaging/unread-count"); setCount(data.unread || 0); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  return count;
}
